/**
 * /reset-password — public route.
 *
 * Reads ?token= from the URL, shows a new-password form, and calls
 * POST /api/auth/reset. On success redirects to /login.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { api } from "@/lib/api";

export const Route = createFileRoute("/reset-password")({
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const navigate = useNavigate();
  const search = new URLSearchParams(window.location.search);
  const token = search.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api("/api/auth/reset", {
        method: "POST",
        body: { token, new_password: password },
      });
      setSuccess(true);
      setTimeout(() => navigate({ to: "/login" }), 2000);
    } catch (exc: unknown) {
      const msg = exc instanceof Error ? exc.message : "Reset failed";
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 dark:bg-gray-800">
        <div className="w-full max-w-sm bg-white rounded-lg shadow p-8 dark:bg-gray-900">
          <p className="text-red-600">Invalid or missing reset token.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 dark:bg-gray-800">
      <div className="w-full max-w-sm bg-white rounded-lg shadow p-8 dark:bg-gray-900">
        <h1 className="text-2xl font-semibold text-slate-900 mb-1 dark:text-slate-100">
          Reset password
        </h1>
        <p className="text-sm text-slate-500 mb-6 dark:text-slate-400">
          Enter your new password below.
        </p>

        {success ? (
          <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2">
            Password reset! Redirecting to sign-in…
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            <label className="block">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                New password
              </span>
              <input
                type="password"
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-700"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8}
                required
                autoFocus
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Confirm password
              </span>
              <input
                type="password"
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-700"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                minLength={8}
                required
              />
            </label>
            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {busy ? "Resetting…" : "Reset password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
